import { Button, Table, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import { CreateQuestionModal } from "../../../components/create-question-modal";
import { PageLayout } from "../../../components/page-layout";
import { UpdateBannerModal } from "../../../components/update-banner-modal";
import { UpdateQuestionModal } from "../../../components/update-question-modal";
import * as api from "../../../utils/api";
import * as S from "./styles";

const DEFAULT_ERROR_MESSAGE =
  "Erro inesperado. Por favor, tente novamente mais tarde.";

export const QuestionsManagementPage = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();

  const [loading, setLoading] = useState(true);

  const [campaignDetails, setCampaignDetails] = useState({});

  const [questions, setQuestions] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);
  const [openUpdateBanner, setOpenUpdateBanner] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const columns = [
    {
      title: "Pergunta",
      key: "question_text",
      dataIndex: "question_text",
    },
    {
      title: "Ações",
      key: "action",
      render: (_, { id }) => (
        <S.ActionsColumnContainer>
          <span
            onClick={() => {
              setSelectedQuestion(questions.find((i) => i.id === id));
              setOpenUpdate(true);
            }}
          >
            Editar
          </span>
          <span
            onClick={() => {
              setSelectedQuestion(questions.find((i) => i.id === id));
              setOpenRemove(true);
            }}
          >
            Remover
          </span>
        </S.ActionsColumnContainer>
      ),
    },
  ];

  const handleGetQuestions = useCallback(async () => {
    setLoading(true);

    try {
      const { result, error } = await api.post("/get-questions", {
        campaignId,
      });

      if (error) {
        throw new Error(error);
      }
      console.log("Result:", result);
      const { campaign, questions } = result;

      const _questions = questions
        ? Array.isArray(questions)
          ? questions
          : [questions]
        : [];

      setQuestions(_questions.map((i, index) => ({ ...i, key: index })));
      setCampaignDetails(campaign);
    } catch (err) {
      message.error(err.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const handleCreateQuestion = async (payload) => {
    try {
      const { error } = await api.post("/create-question", {
        ...payload,
        campaignId,
      });

      if (error) {
        throw new Error(error);
      }

      setOpenCreate(false);
      await handleGetQuestions();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleUpdateQuestion = async (payload) => {
    try {
      const { error } = await api.post("/update-question", {
        ...payload,
        questionId: selectedQuestion.id,
        campaignId,
      });

      if (error) {
        throw new Error(error);
      }

      setOpenUpdate(false);
      await handleGetQuestions();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setSelectedQuestion(null);
    }
  };

  const handleRemoveQuestion = async (e) => {
    e.preventDefault();

    try {
      const { error } = await api.post("/remove-question", {
        questionId: selectedQuestion.id,
        campaignId,
      });

      if (error) {
        throw new Error(error);
      }

      setOpenRemove(false);
      await handleGetQuestions();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setSelectedQuestion(null);
    }
  };

  const handleUpdateBanner = async (files) => {
    try {
      const formData = new FormData();
      formData.append("campaignId", campaignId);

      if (files.desktop) {
        formData.append("bannerFile", files.desktop);
      }
      if (files.mobile) {
        formData.append("bannerFileMobile", files.mobile);
      }

      const { error } = await api.post("/save-banner", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (error) {
        throw new Error(error);
      }

      handleGetQuestions();
      setOpenUpdateBanner(false);
      message.success("Banner atualizado com sucesso!");
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    }
  };

  useEffect(() => {
    handleGetQuestions();
  }, [handleGetQuestions]);

  return (
    <>
      <PageLayout>
        <S.Container>
          <div id="go-back-container">
            <Button type="primary" onClick={() => navigate(-1)} id="app-button">
              Voltar
            </Button>
          </div>

          {campaignDetails?.banner_file_key && (
            <S.BannerContainer
              desktopUrl={`https://plataforma-checkin-banner-files.s3.sa-east-1.amazonaws.com/${campaignDetails?.banner_file_key}`}
              mobileUrl={`https://plataforma-checkin-banner-files.s3.sa-east-1.amazonaws.com/${
                campaignDetails?.banner_mobile_file_key ??
                campaignDetails?.banner_file_key
              }`}
            />
          )}

          <div id="action-container">
            <Button
              type="primary"
              onClick={() => setOpenUpdateBanner(true)}
              id="app-button"
            >
              Atualizar Banner Publicidade
            </Button>
            <Button
              type="primary"
              onClick={() => setOpenCreate(true)}
              id="app-button"
            >
              Registrar
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={questions}
            size="small"
            bordered={true}
            loading={loading}
            pagination={{
              defaultPageSize: 15,
            }}
          />
        </S.Container>
      </PageLayout>
      <CreateQuestionModal
        open={openCreate}
        onCancel={() => setOpenCreate(false)}
        onSubmit={handleCreateQuestion}
      />
      <UpdateQuestionModal
        open={openUpdate}
        onCancel={() => {
          setSelectedQuestion(null);
          setOpenUpdate(false);
        }}
        onSubmit={handleUpdateQuestion}
        selectedQuestion={selectedQuestion}
      />
      <ConfirmationModal
        open={openRemove}
        onCancel={() => {
          setSelectedQuestion(null);
          setOpenRemove(false);
        }}
        onOk={handleRemoveQuestion}
      />
      <UpdateBannerModal
        open={openUpdateBanner}
        onCancel={() => setOpenUpdateBanner(false)}
        onSubmit={handleUpdateBanner}
        campaignId={campaignId}
        title="Atualizar Banner Publicidade"
      />
    </>
  );
};
